import { Title } from '@solidjs/meta'
import { type Component, createMemo, createResource, createSignal, For, Show } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Form from '~/components/Form'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import Switch from '~/components/Switch'
import Table from '~/components/Table'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useDebouncedSignal } from '~/hooks/useDebouncedSignal'
import { usePermissions } from '~/hooks/usePermissions'
import { CRUD_ACTION_LABELS, CRUD_MODULES, groupPermissions, permissionLabel } from '~/services/access.service'
import { type PermissionType, permissionService, type RoleType } from '~/services/permission.service'

import { P } from '~/utils/permissions'

const RolesPage: Component = () => {
  const { can } = usePermissions()
  const alert = useAlert()
  const toast = useToast()

  // Búsqueda server-side
  const [filterSearch, debouncedSearch, setFilterSearch] = useDebouncedSignal('')

  const searchParams = () => {
    const q = debouncedSearch().trim()
    const params: Record<string, string> = {}
    if (q) params.name = `%${q}%`
    return params
  }

  const [roles, { refetch }] = createResource(searchParams, async params => {
    try {
      return (await permissionService.listRoles(params as Parameters<typeof permissionService.listRoles>[0])) as RoleType[]
    } catch {
      return []
    }
  })

  const [allPermissions] = createResource(async () => {
    try {
      return (await permissionService.listPermissions()) as PermissionType[]
    } catch {
      return []
    }
  })

  const [showModal, setShowModal] = createSignal(false)
  const [editing, setEditing] = createSignal<RoleType | null>(null)
  const [isSubmitting, setIsSubmitting] = createSignal(false)
  const [selectedPermissions, setSelectedPermissions] = createSignal<Set<number>>(new Set<number>())

  /** Agrupa los permisos por módulo para la matriz (mismo criterio que la ficha de usuario). */
  const permissionGroups = createMemo(() => groupPermissions(allPermissions() ?? []))

  const openCreate = () => {
    setEditing(null)
    setSelectedPermissions(new Set<number>())
    setShowModal(true)
  }

  const openEdit = (role: RoleType) => {
    setEditing(role)
    const permIds = new Set((role.permissions ?? []).map(p => p.id))
    setSelectedPermissions(permIds)
    setShowModal(true)
  }

  const togglePermission = (permId: number, permName: string) => {
    const current = new Set(selectedPermissions())
    if (current.has(permId)) {
      current.delete(permId)
      // Si se desmarca "update", también desmarcar "delete" del mismo módulo
      if (permName.endsWith('.update')) {
        const module = permName.replace('.update', '')
        const deletePerm = (allPermissions() ?? []).find(p => p.name === `${module}.delete`)
        if (deletePerm) current.delete(deletePerm.id)
      }
    } else {
      current.add(permId)
      // Si se marca "delete", también marcar "update" (regla: eliminar requiere actualizar)
      if (permName.endsWith('.delete')) {
        const module = permName.replace('.delete', '')
        const updatePerm = (allPermissions() ?? []).find(p => p.name === `${module}.update`)
        if (updatePerm) current.add(updatePerm.id)
      }
    }
    setSelectedPermissions(current)
  }

  const handleSubmit = async (formData: FormData) => {
    setIsSubmitting(true)
    try {
      const payload = { name: formData.get('name') as string, description: (formData.get('description') as string) || undefined }
      let roleId: number

      if (editing()) {
        await permissionService.updateRole(editing()!.id, payload)
        roleId = editing()!.id
        toast.addToast({ title: 'Rol actualizado', type: 'success' })
      } else {
        const newRole = await permissionService.createRole(payload)
        roleId = newRole.id
        toast.addToast({ title: 'Rol creado', type: 'success' })
      }

      // Sincronizar permisos
      const currentPerms = new Set((editing()?.permissions ?? []).map(p => p.id))
      const newPerms = selectedPermissions()

      // Añadir nuevos
      for (const permId of newPerms) {
        if (!currentPerms.has(permId)) {
          await permissionService.assignPermissionToRole(roleId, permId)
        }
      }
      // Remover los que ya no están
      for (const permId of currentPerms) {
        if (!newPerms.has(permId)) {
          await permissionService.removePermissionFromRole(roleId, permId)
        }
      }

      setShowModal(false)
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: err instanceof Error ? err.message : 'Error', type: 'danger' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (id: number) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar rol',
      message: '¿Estás seguro de eliminar este rol?'
    })
    if (!ok) return
    try {
      await permissionService.deleteRole(id)
      toast.addToast({ title: 'Rol eliminado', type: 'success' })
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: err instanceof Error ? err.message : 'Error', type: 'danger' })
    }
  }

  const columns = [
    { header: 'Nombre', accessor: (r: RoleType) => r.name },
    { header: 'Descripción', accessor: (r: RoleType) => r.description ?? '-' },
    {
      header: 'Permisos',
      accessor: (r: RoleType) => <Badge color="info">{r.permissions?.length ?? 0}</Badge>
    }
  ]

  return (
    <>
      <Title>Roles y Permisos | Florez Jaramillo</Title>
      <Table<RoleType>
        title="Roles y Permisos"
        subtitle="Gestión de roles y sus permisos de acceso"
        headerActions={
          <div class="flex items-center gap-2">
            <Input
              label="Buscar"
              value={filterSearch()}
              onInput={e => setFilterSearch(e.currentTarget.value)}
              placeholder="Nombre o descripción..."
              startIcon="material-symbols-light:search"
              class="min-w-48"
            />
            <Show when={can(P.roles.create)}>
              <Button onClick={openCreate} prependIcon="material-symbols-light:add-circle-outline">
                Nuevo Rol
              </Button>
            </Show>
          </div>
        }
        data={roles() ?? []}
        columns={columns}
        keyField="id"
        loading={roles.loading}
        emptyMessage="Aún no hay roles"
        actions={role => (
          <ButtonGroup size="sm" variant="soft">
            <Show when={can(P.roles.update)}>
              <Button color="warning" onClick={() => openEdit(role)} tooltip="Editar permisos" icon="material-symbols-light:edit-outline" />
            </Show>
            <Show when={can(P.roles.delete)}>
              <Button color="danger" onClick={() => handleDelete(role.id)} tooltip="Eliminar" icon="material-symbols-light:delete-outline" />
            </Show>
          </ButtonGroup>
        )}
      />

      <Modal isOpen={showModal()} onClose={() => setShowModal(false)} size="lg">
        <Modal.Header>
          <h2 class="text-xl font-bold text-foreground">{editing() ? 'Editar Rol y Permisos' : 'Nuevo Rol'}</h2>
        </Modal.Header>
        <Modal.Body>
          <Form id="role-form" onSubmit={handleSubmit} initialValues={editing() ? { name: editing()!.name, description: editing()!.description ?? '' } : undefined}>
            <div class="space-y-4">
              <Input name="name" label="Nombre del rol" required />
              <Input name="description" label="Descripción" />
            </div>
          </Form>

          <Show when={allPermissions()}>
            <div class="border-t border-border pt-6">
              <h3 class="text-lg font-semibold text-foreground mb-4">Permisos del rol</h3>
              <p class="text-sm text-foreground-subtle mb-4">
                Selecciona los permisos que tendrá este rol. <strong>Nota:</strong> para eliminar registros se requiere el permiso de actualizar.
              </p>

              <div class="space-y-6 max-h-96 overflow-y-auto pr-2">
                <For each={permissionGroups()}>
                  {group => (
                    <div class="rounded-lg border border-border p-4">
                      <h4 class="font-medium text-foreground mb-3">{group.label}</h4>

                      {/* Módulo CRUD: mostrar como tabla */}
                      <Show when={CRUD_MODULES[group.module] && group.permissions.length >= 3}>
                        <div class="grid grid-cols-4 gap-3 text-sm">
                          <For each={['read', 'create', 'update', 'delete']}>
                            {action => {
                              const perm = group.permissions.find(p => p.name.endsWith(`.${action}`))
                              return (
                                <Show when={perm}>
                                  {p => <Switch size="sm" label={CRUD_ACTION_LABELS[action]} checked={selectedPermissions().has(p().id)} onChange={() => togglePermission(p().id, p().name)} />}
                                </Show>
                              )
                            }}
                          </For>
                        </div>
                      </Show>

                      {/* Permisos especiales: lista simple */}
                      {/* Los interruptores son inline: sin flex-col dos permisos largos quedan pegados en la misma línea. */}
                      <Show when={!CRUD_MODULES[group.module] || group.permissions.length < 3}>
                        <div class="flex flex-col gap-3">
                          <For each={group.permissions}>
                            {perm => <Switch size="sm" label={permissionLabel(perm)} checked={selectedPermissions().has(perm.id)} onChange={() => togglePermission(perm.id, perm.name)} />}
                          </For>
                        </div>
                      </Show>
                    </div>
                  )}
                </For>
              </div>
            </div>
          </Show>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button type="button" variant="soft" color="default" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting()} form="role-form">
              {isSubmitting() ? 'Guardando...' : editing() ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <alert.AlertComponent />
    </>
  )
}

export default RolesPage
